import React, {
  useState,
  useEffect,
  useReducer,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { toast } from "react-toastify";

import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { DeleteOutline, Edit, Search } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";

import MainContainer from "../../components/MainContainer";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import FileModal from "../../components/FileModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import ForbiddenPage from "../../components/ForbiddenPage";
import MetricsStrip from "../../components/MetricsStrip";

const reducer = (state, action) => {
  if (action.type === "LOAD_FILES") {
    const files = action.payload;
    const newFiles = [];

    files.forEach((fileList) => {
      const fileListIndex = state.findIndex((item) => item.id === fileList.id);

      if (fileListIndex !== -1) {
        state[fileListIndex] = fileList;
      } else {
        newFiles.push(fileList);
      }
    });

    return [...state, ...newFiles];
  }

  if (action.type === "UPDATE_FILES") {
    const fileList = action.payload;
    const fileListIndex = state.findIndex((item) => item.id === fileList.id);

    if (fileListIndex !== -1) {
      state[fileListIndex] = fileList;
      return [...state];
    }

    return [fileList, ...state];
  }

  if (action.type === "DELETE_FILE") {
    const fileListId = action.payload;
    const fileListIndex = state.findIndex((item) => item.id === fileListId);

    if (fileListIndex !== -1) {
      state.splice(fileListIndex, 1);
    }

    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    minWidth: 0,
  },
  contentStack: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    width: "100%",
    minWidth: 0,
  },
  hero: {
    width: "100%",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  heroActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      "& > *": {
        flex: "1 1 100%",
      },
    },
  },
  heroTitle: {
    fontWeight: 600,
    letterSpacing: "-0.01em",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: theme.spacing(1.5),
    width: "100%",
    minWidth: 0,
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  summaryCard: {
    padding: theme.spacing(1.5, 1.75),
    borderRadius: 16,
    minHeight: 92,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
    backgroundColor: theme.palette.background.paper,
  },
  summaryLabel: {
    fontSize: "0.74rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    opacity: 0.7,
  },
  summaryValue: {
    fontSize: "1.5rem",
    fontWeight: 700,
    lineHeight: 1.1,
    marginTop: theme.spacing(0.75),
  },
  filtersPaper: {
    width: "100%",
    minWidth: 0,
    padding: theme.spacing(1.5),
    borderRadius: 18,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  filtersHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  filtersTitle: {
    fontWeight: 600,
  },
  filtersActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "stretch",
      "& > *": {
        flex: "1 1 100%",
      },
    },
  },
  tableSearch: {
    width: 320,
    maxWidth: "100%",
  },
  tablePaper: {
    borderRadius: 18,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  tableHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(1.25, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  tableHeaderTitle: {
    fontWeight: 600,
  },
  tableContainer: {
    width: "100%",
    minWidth: 0,
    maxHeight: "68vh",
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  tableHeadCell: {
    fontWeight: 600,
  },
  fileNameCell: {
    minWidth: 220,
  },
  fileName: {
    fontWeight: 600,
  },
  fileMeta: {
    opacity: 0.72,
  },
  actionsCell: {
    whiteSpace: "nowrap",
  },
  emptyState: {
    padding: theme.spacing(4, 2),
    textAlign: "center",
    opacity: 0.72,
  },
  loadMoreRow: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(1.5),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

const FileLists = () => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedFileList, setSelectedFileList] = useState(null);
  const [deletingFileList, setDeletingFileList] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [files, dispatch] = useReducer(reducer, []);
  const [fileListModalOpen, setFileListModalOpen] = useState(false);

  const fetchFileLists = useCallback(async (currentSearch = "", currentPage = 1) => {
    try {
      const { data } = await api.get("/files/", {
        params: { searchParam: currentSearch, pageNumber: currentPage },
      });

      dispatch({ type: "LOAD_FILES", payload: data.files || [] });
      setHasMore(Boolean(data.hasMore));
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);

    const delayDebounceFn = setTimeout(() => {
      fetchFileLists(searchParam, pageNumber);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [fetchFileLists, pageNumber, searchParam]);

  useEffect(() => {
    const onFileEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_FILES", payload: data.files });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_FILE", payload: +data.fileId });
      }
    };

    socket.on(`company-${user.companyId}-file`, onFileEvent);

    return () => {
      socket.off(`company-${user.companyId}-file`, onFileEvent);
    };
  }, [socket, user.companyId]);

  const handleOpenFileListModal = () => {
    setSelectedFileList(null);
    setFileListModalOpen(true);
  };

  const handleCloseFileListModal = () => {
    setSelectedFileList(null);
    setFileListModalOpen(false);
  };

  const handleSearch = (event) => {
    const value = event.target.value.toLowerCase();

    dispatch({ type: "RESET" });
    setSearchParam(value);
    setPageNumber(1);
  };

  const handleEditFileList = (fileList) => {
    setSelectedFileList(fileList);
    setFileListModalOpen(true);
  };

  const handleDeleteFileList = async (fileListId) => {
    try {
      await api.delete(`/files/${fileListId}`);
      toast.success(i18n.t("files.toasts.deleted"));
      dispatch({ type: "DELETE_FILE", payload: fileListId });
    } catch (err) {
      toastError(err);
    } finally {
      setConfirmModalOpen(false);
      setDeletingFileList(null);
    }
  };

  const handleRefresh = async () => {
    dispatch({ type: "RESET" });
    setLoading(true);

    if (pageNumber !== 1) {
      setPageNumber(1);
      return;
    }

    await fetchFileLists(searchParam, 1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (event) => {
    if (!hasMore || loading) return;

    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;

    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const summaryCards = useMemo(
    () => [
      { key: "files", label: "Arquivos", value: files.length },
      { key: "visible", label: "Visiveis", value: files.length },
      { key: "pages", label: "Mais paginas", value: hasMore ? "Sim" : "Nao" },
    ],
    [files.length, hasMore]
  );

  if (user.profile === "user") {
    return (
      <MainContainer className={classes.mainContainer} fullWidth>
        <ForbiddenPage />
      </MainContainer>
    );
  }

  return (
    <MainContainer className={classes.mainContainer} fullWidth>
      <ConfirmationModal
        title={i18n.t("files.confirmationModal.deleteTitle")}
        open={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setDeletingFileList(null);
        }}
        onConfirm={() => handleDeleteFileList(deletingFileList.id)}
      >
        {i18n.t("files.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <FileModal
        open={fileListModalOpen}
        onClose={handleCloseFileListModal}
        reload={handleRefresh}
        aria-labelledby="form-dialog-title"
        fileListId={selectedFileList && selectedFileList.id}
      />

      <div className={classes.contentStack}>
        <Box className={classes.hero}>
          <Box>
            <Typography variant="h5" className={classes.heroTitle}>
              {i18n.t("files.title")}
            </Typography>
          </Box>

          <Box className={classes.heroActions}>
            <Button variant="outlined" color="primary" onClick={handleRefresh} disabled={loading}>
              Atualizar
            </Button>
            <Button variant="contained" color="primary" onClick={handleOpenFileListModal}>
              {i18n.t("files.buttons.add")}
            </Button>
          </Box>
        </Box>

        <MetricsStrip ariaLabel="Indicadores de arquivos" items={summaryCards} />

        <Paper className={classes.tablePaper} variant="outlined">
          <Box className={classes.tableHeader}>
            <Typography variant="body1" className={classes.tableHeaderTitle}>
              Arquivos operacionais
            </Typography>
            <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={8}>
              <TextField
                className={classes.tableSearch}
                variant="outlined"
                size="small"
                placeholder={i18n.t("contacts.searchPlaceholder")}
                type="search"
                value={searchParam}
                onChange={handleSearch}
                inputProps={{ "aria-label": "Pesquisar arquivos" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Chip size="small" label={`${files.length} visiveis`} />
            </Box>
          </Box>

          <TableContainer className={classes.tableContainer} onScroll={handleScroll}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableHeadCell}>{i18n.t("files.table.name")}</TableCell>
                  <TableCell align="center" className={classes.tableHeadCell}>
                    {i18n.t("files.table.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {files.map((fileList) => (
                  <TableRow key={fileList.id} hover>
                    <TableCell className={classes.fileNameCell}>
                      <Typography className={classes.fileName}>{fileList.name}</Typography>
                      <Typography variant="body2" className={classes.fileMeta}>
                        ID {fileList.id}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" className={classes.actionsCell}>
                      <IconButton size="small" onClick={() => handleEditFileList(fileList)}>
                        <Edit />
                      </IconButton>

                      <IconButton
                        size="small"
                        onClick={() => {
                          setConfirmModalOpen(true);
                          setDeletingFileList(fileList);
                        }}
                      >
                        <DeleteOutline />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}

                {loading ? <TableRowSkeleton columns={2} /> : null}

                {!loading && files.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2} className={classes.emptyState}>
                      Nenhum arquivo encontrado.
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>

          {!loading && hasMore && files.length > 0 ? (
            <Box className={classes.loadMoreRow}>
              <Button variant="outlined" color="primary" onClick={loadMore}>
                Carregar mais
              </Button>
            </Box>
          ) : null}
        </Paper>
      </div>
    </MainContainer>
  );
};

export default FileLists;
